// Dark theme for SoftLavka AI ERP

import { createTheme, Theme } from '@mui/material/styles';
import { ThemeConfig } from '@/types';

export const darkThemeConfig: ThemeConfig = {
  name: 'dark',
  displayName: 'Dark',
  colors: {
    primary: '#90caf9',
    secondary: '#f48fb1',
    background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
    surface: 'rgba(255, 255, 255, 0.05)',
    text: '#ffffff',
    textSecondary: 'rgba(255, 255, 255, 0.6)',
    border: 'rgba(255, 255, 255, 0.12)',
    success: '#66bb6a',
    warning: '#ffa726',
    error: '#f44336',
    info: '#29b6f6',
  },
  glassmorphism: {
    background: 'rgba(255, 255, 255, 0.05)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    borderRadius: '16px',
    boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.5)',
  },
};

export const createDarkTheme = (): Theme => {
  return createTheme({
    palette: {
      mode: 'dark',
      primary: {
        main: darkThemeConfig.colors.primary,
        light: '#bbdefb',
        dark: '#5d99c6',
        contrastText: '#000000',
      },
      secondary: {
        main: darkThemeConfig.colors.secondary,
        light: '#f8bbd9',
        dark: '#c2185b',
        contrastText: '#000000',
      },
      background: {
        default: '#121212',
        paper: darkThemeConfig.colors.surface,
      },
      text: {
        primary: darkThemeConfig.colors.text,
        secondary: darkThemeConfig.colors.textSecondary,
      },
      success: {
        main: darkThemeConfig.colors.success,
        light: '#81c784',
        dark: '#388e3c',
      },
      warning: {
        main: darkThemeConfig.colors.warning,
        light: '#ffb74d',
        dark: '#f57c00',
      },
      error: {
        main: darkThemeConfig.colors.error,
        light: '#e57373',
        dark: '#d32f2f',
      },
      info: {
        main: darkThemeConfig.colors.info,
        light: '#4fc3f7',
        dark: '#0288d1',
      },
      divider: darkThemeConfig.colors.border,
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontSize: '2.5rem',
        fontWeight: 600,
        color: darkThemeConfig.colors.text,
      },
      h2: {
        fontSize: '2rem',
        fontWeight: 600,
        color: darkThemeConfig.colors.text,
      },
      h3: {
        fontSize: '1.75rem',
        fontWeight: 500,
        color: darkThemeConfig.colors.text,
      },
      h4: {
        fontSize: '1.5rem',
        fontWeight: 500,
        color: darkThemeConfig.colors.text,
      },
      h5: {
        fontSize: '1.25rem',
        fontWeight: 500,
        color: darkThemeConfig.colors.text,
      },
      h6: {
        fontSize: '1rem',
        fontWeight: 500,
        color: darkThemeConfig.colors.text,
      },
      body1: {
        fontSize: '1rem',
        color: darkThemeConfig.colors.text,
      },
      body2: {
        fontSize: '0.875rem',
        color: darkThemeConfig.colors.textSecondary,
      },
      button: {
        textTransform: 'none',
        fontWeight: 500,
      },
    },
    shape: {
      borderRadius: 12,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            background: darkThemeConfig.colors.background,
            minHeight: '100vh',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '4px',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.3)',
              },
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            background: darkThemeConfig.glassmorphism.background,
            backdropFilter: darkThemeConfig.glassmorphism.backdropFilter,
            WebkitBackdropFilter: darkThemeConfig.glassmorphism.backdropFilter,
            border: darkThemeConfig.glassmorphism.border,
            borderRadius: darkThemeConfig.glassmorphism.borderRadius,
            boxShadow: darkThemeConfig.glassmorphism.boxShadow,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            background: 'rgba(255, 255, 255, 0.03)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: '12px',
            boxShadow: '0 4px 16px 0 rgba(0, 0, 0, 0.3)',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 24px 0 rgba(0, 0, 0, 0.4)',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
            padding: '10px 24px',
            textTransform: 'none',
            fontWeight: 500,
            transition: 'all 0.3s ease',
            '&.Mui-disabled': {
              opacity: 1,
            },
            '&:hover': {
              transform: 'translateY(-1px)',
            },
          },
          contained: {
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
            '&.Mui-disabled': {
              color: '#ffffff',
              background: '#4f7fb4',
              boxShadow: 'none',
            },
            '&:hover': {
              boxShadow: '0 6px 24px rgba(0, 0, 0, 0.3)',
            },
          },
          outlined: {
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            '&.Mui-disabled': {
              color: 'rgba(255, 255, 255, 0.75)',
              border: '1px solid rgba(255, 255, 255, 0.25)',
              background: 'rgba(255, 255, 255, 0.06)',
            },
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.08)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
            },
          },
          text: {
            '&.Mui-disabled': {
              color: 'rgba(255, 255, 255, 0.7)',
            },
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.05)',
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '12px',
              '& fieldset': {
                border: '1px solid rgba(255, 255, 255, 0.12)',
              },
              '&:hover fieldset': {
                border: '1px solid rgba(255, 255, 255, 0.2)',
              },
              '&.Mui-focused fieldset': {
                border: '2px solid #90caf9',
              },
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '12px',
            '& .MuiOutlinedInput-notchedOutline': {
              border: '1px solid rgba(255, 255, 255, 0.12)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              border: '1px solid rgba(255, 255, 255, 0.2)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              border: '2px solid #90caf9',
            },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            background: 'rgba(30, 30, 30, 0.95)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            borderRadius: '12px',
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.5)',
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.08)',
            },
            '&.Mui-selected': {
              background: 'rgba(144, 202, 249, 0.2)',
              '&:hover': {
                background: 'rgba(144, 202, 249, 0.3)',
              },
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            background: 'rgba(30, 30, 30, 0.9)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            boxShadow: '0 4px 16px 0 rgba(0, 0, 0, 0.3)',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            background: 'rgba(20, 20, 20, 0.95)',
            backdropFilter: 'blur(15px)',
            WebkitBackdropFilter: 'blur(15px)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: '0 16px 16px 0',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            background: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.12)',
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            borderRadius: '12px',
          },
        },
      },
    },
  });
};




